type Stop = "floor" | "between" | "ceiling";

interface Preference {
  version: 1;
  stop: Stop;
  progress: number;
}

interface Drag {
  pointerId: number;
  startY: number;
  lastY: number;
  startHeight: number;
  moved: boolean;
  samples: { y: number; time: number }[];
}

const STORAGE_KEY = "webscan.scan-panel.height";
const FLICK_SPEED = 0.5; // CSS pixels per millisecond, measured over the final 100 ms.
const DRAG_DISTANCE = 5;
const KEY_STEPS: Partial<Record<string, number>> = {
  ArrowUp: 0.05,
  ArrowRight: 0.05,
  ArrowDown: -0.05,
  ArrowLeft: -0.05,
  PageUp: 0.2,
  PageDown: -0.2,
};

function readPreference(): Preference {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (saved && typeof saved === "object") {
      const value = saved as Partial<Preference>;
      if (
        value.version === 1 &&
        (value.stop === "floor" || value.stop === "between" || value.stop === "ceiling") &&
        typeof value.progress === "number" &&
        Number.isFinite(value.progress) &&
        value.progress >= 0 &&
        value.progress <= 1
      ) {
        return {
          version: 1,
          stop: value.stop,
          progress: value.stop === "floor" ? 0 : value.stop === "ceiling" ? 1 : value.progress,
        };
      }
    }
  } catch {
    // A blocked or damaged preference must never prevent camera access.
  }
  return { version: 1, stop: "floor", progress: 0 };
}

/** The session sheet owns its height, independently of modal panels and camera framing. */
export function mountScanPanel(
  panel: HTMLElement,
  dragRow: HTMLElement,
  featuredHost: HTMLElement,
  list: HTMLElement,
): { refresh: () => void; destroy: () => void } {
  const root = panel.closest<HTMLElement>(".scan-root") ?? panel.parentElement!;
  let preference = readPreference();
  let floor = 0;
  let ceiling = 0;
  let height = 0;
  let targetStop: Stop = "floor";
  let boxExtra = 0;
  let contentBox = false;
  let drag: Drag | null = null;
  let measureFrame = 0;
  let visibilityFrame = 0;
  let suppressClick = false;
  const observed = new Set<Element>();
  const number = (value: string): number => Number.parseFloat(value) || 0;

  dragRow.setAttribute("role", "slider");
  dragRow.setAttribute("aria-orientation", "vertical");
  dragRow.setAttribute("aria-valuemin", "0");
  dragRow.setAttribute("aria-valuemax", "100");
  dragRow.setAttribute(
    "aria-keyshortcuts",
    "ArrowUp ArrowRight ArrowDown ArrowLeft PageUp PageDown Home End",
  );
  if (!dragRow.hasAttribute("aria-label"))
    dragRow.setAttribute("aria-label", "How much of the session shows");
  dragRow.tabIndex = 0;

  const updateVisibility = (): void => {
    visibilityFrame = 0;
    const renderedHeight = panel.getBoundingClientRect().height;
    const stop: Stop =
      renderedHeight <= floor + 0.5
        ? "floor"
        : renderedHeight >= ceiling - 0.5
          ? "ceiling"
          : "between";
    // During a snap the list follows the rendered height, not the destination.
    panel.dataset.stop = stop;
    list.style.overflowY = stop === "ceiling" ? "auto" : "clip";
    list.inert = stop === "floor";
    const bounds = list.getBoundingClientRect();
    for (const child of list.children) {
      if (!(child instanceof HTMLElement)) continue;
      const rect = child.getBoundingClientRect();
      child.inert =
        stop !== "ceiling" &&
        (stop === "floor" || rect.bottom <= bounds.top || rect.top >= bounds.bottom);
    }
    if (renderedHeight > 0 && Math.abs(renderedHeight - height) > 0.5)
      visibilityFrame = requestAnimationFrame(updateVisibility);
  };

  const apply = (requested: number): void => {
    height = Math.max(floor, Math.min(ceiling, requested));
    if (height - floor < 0.5) height = floor;
    if (ceiling - height < 0.5) height = ceiling;
    const stop: Stop = height <= floor ? "floor" : height >= ceiling ? "ceiling" : "between";
    targetStop = stop;
    const progress = ceiling > floor ? (height - floor) / (ceiling - floor) : 0;
    panel.style.height = `${Math.max(0, height - (contentBox ? boxExtra : 0))}px`;
    dragRow.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
    dragRow.setAttribute(
      "aria-valuetext",
      stop === "floor"
        ? featuredHost.hidden
          ? "Session summary only"
          : "Newest find only"
        : stop === "ceiling"
          ? "Full session list"
          : `${Math.round(progress * 100)}% expanded`,
    );
    cancelAnimationFrame(visibilityFrame);
    updateVisibility();
  };

  const remember = (): void => {
    preference = {
      version: 1,
      stop: targetStop,
      progress: ceiling > floor ? (height - floor) / (ceiling - floor) : preference.progress,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
    } catch {
      // Dragging still works when browser storage is unavailable or full.
    }
  };

  const measure = (): void => {
    measureFrame = 0;
    const rootHeight = root.getBoundingClientRect().height;
    if (!rootHeight) return; // The initial preparation screen may still hide the workspace.
    const style = getComputedStyle(panel);
    boxExtra =
      number(style.paddingTop) +
      number(style.paddingBottom) +
      number(style.borderTopWidth) +
      number(style.borderBottomWidth);
    contentBox = style.boxSizing !== "border-box";
    const items: HTMLElement[] = [];
    const collect = (parent: HTMLElement): void => {
      for (const child of parent.children) {
        if (!(child instanceof HTMLElement) || child.hidden || child === list) continue;
        const childStyle = getComputedStyle(child);
        if (childStyle.display === "none") continue;
        if (childStyle.display === "contents") collect(child);
        else if (childStyle.position !== "absolute" && childStyle.position !== "fixed")
          items.push(child);
      }
    };
    collect(panel);
    let natural = boxExtra + Math.max(0, items.length - 1) * number(style.rowGap);
    for (const item of items) {
      const itemStyle = getComputedStyle(item);
      natural +=
        item.getBoundingClientRect().height +
        number(itemStyle.marginTop) +
        number(itemStyle.marginBottom);
    }
    const nextObserved = new Set<Element>([root, dragRow, featuredHost, ...items]);
    for (const item of observed) {
      if (!nextObserved.has(item)) {
        resizeObserver.unobserve(item);
        observed.delete(item);
      }
    }
    for (const item of nextObserved) {
      if (!observed.has(item)) {
        resizeObserver.observe(item);
        observed.add(item);
      }
    }
    // The ceiling is a border-box limit even if application styles use content-box.
    ceiling = rootHeight * 0.8;
    floor = Math.min(natural, ceiling);
    apply(drag ? height : floor + preference.progress * (ceiling - floor));
    if (drag) {
      drag.startHeight = height;
      drag.startY = drag.lastY;
    }
  };

  const scheduleMeasure = (): void => {
    if (!measureFrame) measureFrame = requestAnimationFrame(measure);
  };
  const resizeObserver = new ResizeObserver(scheduleMeasure);
  resizeObserver.observe(root);
  observed.add(root);
  const mutations = new MutationObserver(scheduleMeasure);
  mutations.observe(panel, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["hidden", "class"],
  });

  const sample = (event: PointerEvent): void => {
    if (!drag) return;
    const samples = drag.samples;
    if (samples.length && event.timeStamp - samples.at(-1)!.time > 100) samples.length = 0;
    samples.push({ y: event.clientY, time: event.timeStamp });
    while (samples.length > 2 && samples[1].time < event.timeStamp - 100) samples.shift();
  };

  const down = (event: PointerEvent): void => {
    if (drag || !event.isPrimary || event.button !== 0) return;
    measure();
    const displayedHeight = panel.getBoundingClientRect().height;
    drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      startHeight: displayedHeight,
      moved: false,
      samples: [{ y: event.clientY, time: event.timeStamp }],
    };
    panel.classList.add("is-dragging");
    panel.dataset.dragging = "true";
    apply(displayedHeight);
    dragRow.focus({ preventScroll: true });
    dragRow.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const move = (event: PointerEvent): void => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.lastY = event.clientY;
    const distance = drag.startY - event.clientY;
    drag.moved ||= Math.abs(distance) >= DRAG_DISTANCE;
    sample(event);
    apply(drag.startHeight + distance);
    event.preventDefault();
  };

  const finish = (event?: PointerEvent): void => {
    if (!drag || (event && drag.pointerId !== event.pointerId)) return;
    if (event?.type === "pointerup") {
      move(event);
    }
    const current = drag;
    drag = null;
    let destination = height;
    if (event?.type === "pointerup" && current.moved && current.samples.length > 1) {
      const first = current.samples[0];
      const last = current.samples.at(-1)!;
      const elapsed = last.time - first.time;
      const velocity = elapsed > 0 ? (first.y - last.y) / elapsed : 0;
      if (Math.abs(velocity) >= FLICK_SPEED) destination = velocity > 0 ? ceiling : floor;
    }
    panel.classList.remove("is-dragging");
    panel.dataset.dragging = "false";
    apply(destination);
    if (current.moved) {
      suppressClick = true;
      remember();
    }
    if (dragRow.hasPointerCapture(current.pointerId))
      dragRow.releasePointerCapture(current.pointerId);
  };

  const clearClick = (): void => {
    suppressClick = false;
  };
  const click = (event: MouseEvent): void => {
    if (!suppressClick || event.detail === 0) return;
    suppressClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  const keydown = (event: KeyboardEvent): void => {
    if (event.target !== dragRow || drag || event.ctrlKey || event.metaKey || event.altKey) return;
    const step = KEY_STEPS[event.key];
    if (step === undefined && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    // Measure restores the requested preference, so repeated keys accumulate even
    // while the panel is still animating toward the preceding key's destination.
    measure();
    apply(
      event.key === "Home"
        ? floor
        : event.key === "End"
          ? ceiling
          : height + (step ?? 0) * (ceiling - floor),
    );
    remember();
  };
  const blur = (): void => finish();

  document.addEventListener("pointerdown", clearClick, true);
  panel.addEventListener("click", click, true);
  dragRow.addEventListener("pointerdown", down);
  dragRow.addEventListener("pointermove", move);
  dragRow.addEventListener("pointerup", finish);
  dragRow.addEventListener("pointercancel", finish);
  dragRow.addEventListener("lostpointercapture", finish);
  dragRow.addEventListener("keydown", keydown);
  window.addEventListener("blur", blur);
  window.addEventListener("resize", scheduleMeasure);
  window.visualViewport?.addEventListener("resize", scheduleMeasure);
  measure();

  return {
    refresh: scheduleMeasure,
    destroy: () => {
      finish();
      cancelAnimationFrame(measureFrame);
      cancelAnimationFrame(visibilityFrame);
      resizeObserver.disconnect();
      mutations.disconnect();
      document.removeEventListener("pointerdown", clearClick, true);
      panel.removeEventListener("click", click, true);
      dragRow.removeEventListener("pointerdown", down);
      dragRow.removeEventListener("pointermove", move);
      dragRow.removeEventListener("pointerup", finish);
      dragRow.removeEventListener("pointercancel", finish);
      dragRow.removeEventListener("lostpointercapture", finish);
      dragRow.removeEventListener("keydown", keydown);
      window.removeEventListener("blur", blur);
      window.removeEventListener("resize", scheduleMeasure);
      window.visualViewport?.removeEventListener("resize", scheduleMeasure);
    },
  };
}
